import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaPlus, FaSyncAlt, FaEdit, FaTrash } from "react-icons/fa";
import {
  addVariant,
  deleteVariant,
  deleteVariantValue,
  fetchVariants,
  fetchVariantValues,
  updateVariant,
  updateVariantValue,
  addVariantValue,
} from "../../redux/Slices/VariantSlice";
import { showConfirm } from "../../Utils/SwalConfirm";
import { useTitle } from "../../context/TitleContext";
import { validateFieldLanguage } from "../../Utils/languageValidation";

const emptyVariant = { id: null, name_ar: "", name_en: "", key: "" };
const emptyValue = { id: null, name_ar: "", name_en: "", variant_id: "" };

const getLocalizedName = (record, locale) =>
  record?.name?.[locale] ??
  record?.[`name_${locale}`] ??
  record?.translations?.find((item) => item.locale === locale)?.name ??
  "—";

const Variants = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { variants, variantValues, loading, actionLoading } = useSelector(
    (state) => state.variants
  );
  const [activeTab, setActiveTab] = useState("variants");
  const [variantForm, setVariantForm] = useState(emptyVariant);
  const [valueForm, setValueForm] = useState(emptyValue);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [showValueModal, setShowValueModal] = useState(false);

  useEffect(() => {
    setTitle("خصائص المنتجات");
    dispatch(fetchVariants());
    dispatch(fetchVariantValues());
  }, [dispatch, setTitle]);

  const variantsById = useMemo(
    () =>
      Object.fromEntries(
        variants.map((variant) => [String(variant.id), variant])
      ),
    [variants]
  );

  const refresh = () => {
    dispatch(fetchVariants());
    dispatch(fetchVariantValues());
  };

  const openAddVariant = () => {
    setVariantForm(emptyVariant);
    setShowVariantModal(true);
  };

  const openEditVariant = (variant) => {
    setVariantForm({
      id: variant.id,
      name_ar: getLocalizedName(variant, "ar") === "—" ? "" : getLocalizedName(variant, "ar"),
      name_en: getLocalizedName(variant, "en") === "—" ? "" : getLocalizedName(variant, "en"),
      key: variant.key || "",
    });
    setShowVariantModal(true);
  };

  const openEditValue = (value) => {
    setValueForm({
      id: value.id,
      name_ar: getLocalizedName(value, "ar") === "—" ? "" : getLocalizedName(value, "ar"),
      name_en: getLocalizedName(value, "en") === "—" ? "" : getLocalizedName(value, "en"),
      variant_id: String(value.variant_id ?? value.variant?.id ?? ""),
    });
    setShowValueModal(true);
  };

  const openAddValue = () => {
    setValueForm(emptyValue);
    setShowValueModal(true);
  };

  const submitVariant = async (event) => {
    event.preventDefault();
    const nameAr = variantForm.name_ar.trim();
    const nameEn = variantForm.name_en.trim();
    const key = variantForm.key.trim();

    if (!nameAr || !nameEn || !key) {
      toast.error("جميع حقول الخاصية مطلوبة");
      return;
    }

    const nameRegex = /^[a-zA-Z\u0600-\u06FF\s]+$/;
    if (!nameRegex.test(nameAr) || !nameRegex.test(nameEn) || !nameRegex.test(key)) {
      toast.error("اسم الخاصية ومفتاحها يجب أن يحتويا على حروف فقط بدون أرقام أو رموز");
      return;
    }

    // BUG-65/70 fix: التحقق من لغة حقول اسم الخاصية (منع خلط العربي بالإنجليزي)
    const variantArError = validateFieldLanguage(nameAr, "ar");
    if (variantArError) return toast.error(variantArError);
    const variantEnError = validateFieldLanguage(nameEn, "en");
    if (variantEnError) return toast.error(variantEnError);

    // التحقق من عدم تكرار الاسم بالعربية أو بالإنجليزية أو المفتاح
    const isDuplicateNameAr = variants.some(
      (v) =>
        v.id !== variantForm.id &&
        getLocalizedName(v, "ar").trim().toLowerCase() === nameAr.toLowerCase()
    );
    const isDuplicateNameEn = variants.some(
      (v) =>
        v.id !== variantForm.id &&
        getLocalizedName(v, "en").trim().toLowerCase() === nameEn.toLowerCase()
    );
    const isDuplicateKey = variants.some(
      (v) =>
        v.id !== variantForm.id &&
        (v.key || "").trim().toLowerCase() === key.toLowerCase()
    );

    if (isDuplicateNameAr) {
      toast.error("اسم الخاصية بالعربية مكرر بالفعل");
      return;
    }
    if (isDuplicateNameEn) {
      toast.error("اسم الخاصية بالإنجليزية مكرر بالفعل");
      return;
    }
    if (isDuplicateKey) {
      toast.error("مفتاح الخاصية مكرر بالفعل");
      return;
    }

    try {
      if (variantForm.id) {
        await dispatch(updateVariant(variantForm)).unwrap();
        toast.success("تم تعديل الخاصية بنجاح");
      } else {
        await dispatch(addVariant(variantForm)).unwrap();
        toast.success("تمت إضافة الخاصية بنجاح");
      }
      setShowVariantModal(false);
      refresh();
    } catch (error) {
      toast.error(error || "تعذر حفظ الخاصية");
    }
  };

  const submitValue = async (event) => {
    event.preventDefault();
    const nameAr = valueForm.name_ar.trim();
    const nameEn = valueForm.name_en.trim();
    const variantId = String(valueForm.variant_id);

    if (!nameAr || !nameEn || !variantId) {
      toast.error("جميع حقول قيمة الخاصية مطلوبة");
      return;
    }

    // BUG-65/70 fix: التحقق من لغة حقول قيمة الخاصية (منع خلط العربي بالإنجليزي)
    const valueArError = validateFieldLanguage(nameAr, "ar");
    if (valueArError) return toast.error(valueArError);
    const valueEnError = validateFieldLanguage(nameEn, "en");
    if (valueEnError) return toast.error(valueEnError);

    // التحقق من عدم تكرار القيمة لنفس الخاصية
    const isDuplicateValueAr = variantValues.some(
      (val) =>
        val.id !== valueForm.id &&
        String(val.variant_id ?? val.variant?.id) === variantId &&
        getLocalizedName(val, "ar").trim().toLowerCase() === nameAr.toLowerCase()
    );
    const isDuplicateValueEn = variantValues.some(
      (val) =>
        val.id !== valueForm.id &&
        String(val.variant_id ?? val.variant?.id) === variantId &&
        getLocalizedName(val, "en").trim().toLowerCase() === nameEn.toLowerCase()
    );

    if (isDuplicateValueAr) {
      toast.error("هذه القيمة بالعربية مكررة بالفعل لنفس الخاصية");
      return;
    }
    if (isDuplicateValueEn) {
      toast.error("هذه القيمة بالإنجليزية مكررة بالفعل لنفس الخاصية");
      return;
    }

    try {
      if (valueForm.id) {
        await dispatch(updateVariantValue(valueForm)).unwrap();
        toast.success("تم تعديل قيمة الخاصية بنجاح");
      } else {
        await dispatch(addVariantValue(valueForm)).unwrap();
        toast.success("تمت إضافة قيمة الخاصية بنجاح");
      }
      setShowValueModal(false);
      refresh();
    } catch (error) {
      toast.error(error || "تعذر حفظ قيمة الخاصية");
    }
  };

  const handleDeleteVariant = async (variant) => {
    const confirmed = await showConfirm({
      title: "حذف الخاصية؟",
      text: "لن يتم الحذف إذا كانت الخاصية مرتبطة بمنتجات.",
    });
    if (!confirmed) return;

    try {
      await dispatch(deleteVariant(variant.id)).unwrap();
      toast.success("تم حذف الخاصية بنجاح");
    } catch (error) {
      toast.error(error || "تعذر حذف الخاصية");
    }
  };

  const handleDeleteValue = async (value) => {
    const confirmed = await showConfirm({
      title: "حذف قيمة الخاصية؟",
      text: "لن يتم الحذف إذا كانت القيمة مستخدمة في تركيبة منتج.",
    });
    if (!confirmed) return;

    try {
      await dispatch(deleteVariantValue(value.id)).unwrap();
      toast.success("تم حذف القيمة بنجاح");
    } catch (error) {
      toast.error(error || "تعذر حذف القيمة");
    }
  };

  const labelS = { fontSize: 12, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 6 };
  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1040, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  return (
    <div style={{ padding: "4px 0", direction: "rtl" }}>
      <style>{`
        .var-custom-select {
          width: 100% !important;
          padding: 10px 14px !important;
          padding-left: 40px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          appearance: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>") !important;
          background-repeat: no-repeat !important;
          background-position: left 14px center !important;
          background-size: 16px !important;
          cursor: pointer !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .var-custom-select:focus {
          border-color: var(--red-color) !important;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
        }
        .var-input {
          width: 100%;
          padding: 10px 14px;
          border-radius: 9px;
          border: 1.5px solid #e2e8f0;
          font-size: 14px;
          outline: none;
          box-sizing: border-box;
          font-family: inherit;
          background: #fff;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .var-input:focus {
          border-color: var(--red-color);
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }
        .tab-btn {
          padding: 8px 20px;
          border-radius: 8px;
          border: none;
          background: transparent;
          color: #64748b;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
        }
        .tab-btn.active {
          background: var(--red-color);
          color: #fff;
        }
      `}</style>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            ⚙️ خصائص المنتجات
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>إدارة وتعديل الخصائص كالألوان، والمقاسات، والماركات وقيمها الخاصة.</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={refresh} disabled={loading}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
            <FaSyncAlt size={11} /> تحديث
          </button>
          {activeTab === "variants" ? (
            <button onClick={openAddVariant}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 20px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,var(--main-color),var(--main-light))", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
              <FaPlus size={11} /> إضافة خاصية
            </button>
          ) : (
            <button onClick={openAddValue}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 20px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,var(--main-color),var(--main-light))", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
              <FaPlus size={11} /> إضافة قيمة للخاصية
            </button>
          )}
        </div>
      </div>

      {/* ── Tab Switcher ────────────────────────────────── */}
      <div style={{ display: "flex", gap: 6, background: "#f1f5f9", padding: 4, borderRadius: 10, width: "fit-content", marginBottom: 20 }}>
        <button onClick={() => setActiveTab("variants")} className={`tab-btn${activeTab === "variants" ? " active" : ""}`}>
          📋 الخصائص ({variants.length})
        </button>
        <button onClick={() => setActiveTab("values")} className={`tab-btn${activeTab === "values" ? " active" : ""}`}>
          🏷️ قيم الخصائص ({variantValues.length})
        </button>
      </div>

      {/* ── Loading Skeleton ────────────────────────────── */}
      {loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[...Array(4)].map((_, i) => (
            <div key={i} style={{ height: 60, borderRadius: 12, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
          ))}
          <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
        </div>
      )}

      {/* ── Table Container ────────────────────────────── */}
      {!loading && (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "right" }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                  <th style={{ padding: "14px 20px", fontSize: 13, fontWeight: 700, color: "#475569" }}>#</th>
                  {activeTab === "variants" ? (
                    <>
                      <th style={{ padding: "14px 20px", fontSize: 13, fontWeight: 700, color: "#475569" }}>الاسم بالعربية</th>
                      <th style={{ padding: "14px 20px", fontSize: 13, fontWeight: 700, color: "#475569" }}>الاسم بالإنجليزية</th>
                      <th style={{ padding: "14px 20px", fontSize: 13, fontWeight: 700, color: "#475569" }}>المفتاح</th>
                    </>
                  ) : (
                    <>
                      <th style={{ padding: "14px 20px", fontSize: 13, fontWeight: 700, color: "#475569" }}>القيمة بالعربية</th>
                      <th style={{ padding: "14px 20px", fontSize: 13, fontWeight: 700, color: "#475569" }}>القيمة بالإنجليزية</th>
                      <th style={{ padding: "14px 20px", fontSize: 13, fontWeight: 700, color: "#475569" }}>الخاصية التابعة</th>
                    </>
                  )}
                  <th style={{ padding: "14px 20px", fontSize: 13, fontWeight: 700, color: "#475569", textAlign: "center" }}>الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {activeTab === "variants" ? (
                  variants.length ? variants.map((variant, index) => (
                    <tr key={variant.id} style={{ borderBottom: "1px solid #f1f5f9" }} className="table-row">
                      <td style={{ padding: "14px 20px", fontSize: 14, color: "#64748b" }}>{index + 1}</td>
                      <td style={{ padding: "14px 20px", fontSize: 14, fontWeight: 600, color: "#0f172a" }}>{getLocalizedName(variant, "ar")}</td>
                      <td style={{ padding: "14px 20px", fontSize: 14, color: "#475569" }}>{getLocalizedName(variant, "en")}</td>
                      <td style={{ padding: "14px 20px", fontSize: 14 }}><code style={{ background: "#f1f5f9", padding: "3px 8px", borderRadius: 6, color: "var(--red-color)" }}>{variant.key || "—"}</code></td>
                      <td style={{ padding: "14px 20px", textAlign: "center" }}>
                        <div style={{ display: "inline-flex", gap: 6 }}>
                          <button onClick={() => openEditVariant(variant)} title="تعديل"
                            style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid var(--warning-color)", background: "#fefce8", color: "var(--warning-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FaEdit size={14} />
                          </button>
                          <button onClick={() => handleDeleteVariant(variant)} disabled={actionLoading} title="حذف"
                            style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "var(--red-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FaTrash size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan={5} style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>لا توجد خصائص منتجات</td></tr>
                  )
                ) : (
                  variantValues.length ? variantValues.map((value, index) => {
                    const variant = variantsById[String(value.variant_id ?? value.variant?.id)];
                    return (
                      <tr key={value.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "14px 20px", fontSize: 14, color: "#64748b" }}>{index + 1}</td>
                        <td style={{ padding: "14px 20px", fontSize: 14, fontWeight: 600, color: "#0f172a" }}>{getLocalizedName(value, "ar")}</td>
                        <td style={{ padding: "14px 20px", fontSize: 14, color: "#475569" }}>{getLocalizedName(value, "en")}</td>
                        <td style={{ padding: "14px 20px", fontSize: 14 }}><span style={{ background: "#eff6ff", padding: "4px 10px", borderRadius: 20, color: "#1e40af", fontSize: 12, fontWeight: 700 }}>{getLocalizedName(value.variant || variant, "ar")}</span></td>
                        <td style={{ padding: "14px 20px", textAlign: "center" }}>
                          <div style={{ display: "inline-flex", gap: 6 }}>
                            <button onClick={() => openEditValue(value)} title="تعديل"
                              style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid var(--warning-color)", background: "#fefce8", color: "var(--warning-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <FaEdit size={14} />
                            </button>
                            <button onClick={() => handleDeleteValue(value)} disabled={actionLoading} title="حذف"
                              style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "var(--red-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <FaTrash size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan={5} style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>لا توجد قيم خصائص</td></tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Modal: الخاصية ────────────────────────────── */}
      {showVariantModal && (
        <div style={overlay}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 460, boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 700, fontSize: 16, color: "#0f172a", display: "flex", alignItems: "center", gap: 6 }}>{variantForm.id ? <><FaEdit /> تعديل الخاصية</> : <><FaPlus size={11} /> إضافة خاصية جديدة</>}</span>
              <button onClick={() => setShowVariantModal(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <form onSubmit={submitVariant}>
              <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <label style={labelS}>الاسم بالعربية *</label>
                  <input className="var-input" value={variantForm.name_ar} onChange={(e) => setVariantForm({ ...variantForm, name_ar: e.target.value })} required />
                </div>
                <div>
                  <label style={labelS}>الاسم بالإنجليزية *</label>
                  <input className="var-input" dir="ltr" value={variantForm.name_en} onChange={(e) => setVariantForm({ ...variantForm, name_en: e.target.value })} required />
                </div>
                <div>
                  <label style={labelS}>المفتاح *</label>
                  <input className="var-input" dir="ltr" placeholder="مثال: color" value={variantForm.key} onChange={(e) => setVariantForm({ ...variantForm, key: e.target.value })} required />
                </div>
              </div>
              <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" onClick={() => setShowVariantModal(false)}
                  style={{ padding: "9px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
                <button type="submit" disabled={actionLoading}
                  style={{ padding: "9px 24px", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
                  {actionLoading ? "جاري الحفظ..." : "حفظ"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: القيمة ─────────────────────────────── */}
      {showValueModal && (
        <div style={overlay}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 460, boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 700, fontSize: 16, color: "#0f172a", display: "flex", alignItems: "center", gap: 6 }}>{valueForm.id ? <><FaEdit /> تعديل قيمة الخاصية</> : <><FaPlus size={11} /> إضافة قيمة جديدة للخاصية</>}</span>
              <button onClick={() => setShowValueModal(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <form onSubmit={submitValue}>
              <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <label style={labelS}>الخاصية التابعة *</label>
                  <select className="var-custom-select" value={valueForm.variant_id} onChange={(e) => setValueForm({ ...valueForm, variant_id: e.target.value })} required>
                    <option value="">اختر الخاصية</option>
                    {variants.map((variant) => <option key={variant.id} value={variant.id}>{getLocalizedName(variant, "ar")}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelS}>القيمة بالعربية *</label>
                  <input className="var-input" value={valueForm.name_ar} onChange={(e) => setValueForm({ ...valueForm, name_ar: e.target.value })} required />
                </div>
                <div>
                  <label style={labelS}>القيمة بالإنجليزية *</label>
                  <input className="var-input" dir="ltr" value={valueForm.name_en} onChange={(e) => setValueForm({ ...valueForm, name_en: e.target.value })} required />
                </div>
              </div>
              <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" onClick={() => setShowValueModal(false)}
                  style={{ padding: "9px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
                <button type="submit" disabled={actionLoading}
                  style={{ padding: "9px 24px", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
                  {actionLoading ? "جاري الحفظ..." : "حفظ"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Variants;
